import React, { useEffect, useState, useRef } from "react"
import Api from "../../../../_helpers/Api"
import Skeleton from "react-loading-skeleton"
import { trans } from "../../../../_providers/Translation"
import "../../../../assets/css/partials/status-diagram.css"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = { employee_id: "", date_from: null, date_to: null }

export default function ResolvedTickets() {
  const [state, setState] = useState({ loading: true, data: null })
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  const loadData = (currentFilters) => {
    setState((prev) => ({ ...prev, loading: true }))

    const f = `${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`

    Api.get(
      `analytics/ticket-resolution?employee_id=${currentFilters.employee_id}${f}`,
    )
      .then((res) => {
        setState({
          loading: false,
          data: res.data.data,
          employees: res.data.employees,
        })
      })
      .catch(() => setState({ loading: false, data: null }))
  }

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData(filters)
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  const itemsSource = [
    {
      key: "normal",
      label: trans("analytics.normalTickets"),
      value: state.data?.normal.count || 0,
      color: "#6B84DF",
    },
    {
      key: "priority",
      label: trans("analytics.highPriorityTickets"),
      value: state.data?.priority.count || 0,
      color: "#EC5252",
    },
    {
      key: "unsolved",
      label: trans("analytics.unsolvedTickets"),
      value: state.data?.unsolved.count || 0,
      color: "#C6D2FD",
    },
  ]

  const items = itemsSource

  function getTotalResults() {
    return items.reduce((sum, it) => sum + Number(it.value || 0), 0)
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.employee_id) count++
    if (filters.date_from || filters.date_to) count++
    return count
  }

  return (
    <div className="resolved-tickets-chart">
      <div className="head">
        <h3>{trans("analytics.ticketResolution")}</h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.employee_id || ""}
                  name="employee_id"
                  onChange={(e) =>
                    handleFilterChange("employee_id", e.target.value)
                  }
                >
                  <option value="">{trans("random.all")}</option>
                  {state.employees?.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {state.loading ? (
        <Skeleton height={200} count={1} />
      ) : getTotalResults() === 0 ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <div className="body">
          <div className="bubble-chart">
            {(() => {
              const maxSize = 200
              const minSize = 70
              const sorted = [...items].sort(
                (a, b) => Number(b.value) - Number(a.value),
              )
              const maxVal = Math.max(...sorted.map((i) => Number(i.value)), 1)

              const bubbles = sorted.map((it) => {
                const ratio = Number(it.value) / maxVal
                const diameter =
                  minSize + (maxSize - minSize) * Math.sqrt(ratio)
                return { ...it, diameter }
              })

              // Positions: largest left, medium top-right, smallest bottom-right
              const positions = [
                { top: "10%", left: "0" },
                { top: "0", right: "0" },
                { bottom: "0", right: "5%" },
              ]

              return bubbles.map((b, idx) => (
                <div
                  key={b.key}
                  className="bubble"
                  style={{
                    width: b.diameter,
                    height: b.diameter,
                    backgroundColor: b.color,
                    border: "4px solid #fff",
                    ...positions[idx],
                  }}
                >
                  {b.value}
                </div>
              ))
            })()}
          </div>

          <ul>
            {items.map((it, idx) => {
              return (
                <li key={it.key || it.label || idx}>
                  <span style={{ backgroundColor: it.color }} />
                  {it.label}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
